import * as React from "react";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../../Shared/Process/Process/ProcessComponent";
import { RadiosEditor } from "../../../../../../../../react-core/Html/RadiosEditor";
import IncomePayment from "./IncomePayment";
import { DrawdownType } from "../../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { quoteSteppersMap } from "../../../Resource/Content";
import { quoteSippIncomePaymentDetailsLabels } from "../../../Resource/Content";
import { PensionArrangementType } from "../../../../../../../../Wrap/Constants/Enums/Domain/PensionArrangementType";
import { setValueByKey } from "../../../../../../../Shared/Table/Utils/TableUtils";
import { QuoteModel } from "../../../../../../../../Wrap/Models/Models/Processes/Quote/QuoteModel";
import { OneOffIncomePayment } from "./OneOffIncomePayment";
import { PensionQuote } from "../../../../../../../../Wrap/Models/Models/Processes/Quote/Pension/PensionQuote";
import { PensionType } from "../../../../../../../../Wrap/Constants/Enums/Domain/PensionType";
import { SubAccountType } from "../../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";

interface IIncomePaymentDetailsProps extends IProcessComponentProps {
    data: QuoteModel;
}

interface IIncomePaymentDetailsState {}

export class IncomePaymentDetails extends ProcessComponent<IIncomePaymentDetailsProps, IIncomePaymentDetailsState> {
    constructor(props: any) {
        super(props);
        this.state = {};
    }

    private hasDrawdown(data: QuoteModel): boolean {
        return !!data?.PensionQuote?.DrawdownModel?.InstructionId;
    }

    private hasTransfer(data: QuoteModel): boolean {
        return data?.PensionQuote?.PensionTransfers?.length > 0;
    }

    private handleOnChange(changes) {
        const { onChange, data } = this.props;
        let _data = JSON.parse(JSON.stringify(data?.PensionQuote));
        if (this.hasDrawdown(data)) {
            for (const key in changes) {
                _data = setValueByKey(_data, `DrawdownModel.Income.${key}`, changes[key]);
            }
        }
        if (this.hasTransfer(data)) {
            _data.PensionTransfers = _data?.PensionTransfers?.map((e) => {
                for (const key in changes) {
                    e = setValueByKey(e, `IncomePaymentDetails.DrawdownIncome.AnnualIncome.${key}`, changes[key]);
                }
                return e;
            });
        }
        this.onDataChange(onChange, _data, data, "PensionQuote");
    }

    private shouldShowIncomePayment() {
        const { data } = this.props;
        return (
            (data?.PensionQuote?.DrawdownModel &&
                (data?.PensionQuote?.DrawdownModel?.DrawdownType !== DrawdownType.Regular ||
                    data?.PensionQuote?.DrawdownModel?.Income?.TaxableIncomeRequiredInAddition)) ||
            (data?.PensionQuote?.PensionTransfers || []).some(
                (e) => e?.PensionSource == PensionArrangementType.Crystallised
            )
        );
    }

    private handleOneOffChange(changes) {
        const { onChange, data } = this.props;
        let _data: PensionQuote = JSON.parse(JSON.stringify(data?.PensionQuote));
        if (this.hasDrawdown(data)) {
            for (const key in changes) {
                _data = setValueByKey(_data, `DrawdownModel.OneOffTaxableWithdrawalDetails.${key}`, changes[key]);
            }
        }
        if (this.hasTransfer(data)) {
            _data.PensionTransfers = _data?.PensionTransfers?.map((e) => {
                for (const key in changes) {
                    e = setValueByKey(e, `OneOffTaxableWithdrawalDetails.${key}`, changes[key]);
                }
                return e;
            });
        }
        this.onDataChange(onChange, _data, data, "PensionQuote");
    }

    private renderIncomePayment() {
        const { data } = this.props;
        const isImmediateTaxFreeLumpSum =
            data?.SelectedProducts?.find((ele) => ele.SubAccountType == SubAccountType.JuniorSipp)
                ?.PensionType == PensionType.ImmediateTaxFreeLumpSum;
        const postTransfer = data?.PensionQuote?.PensionTransfers
            ?.find(trans => trans.PensionSource == PensionArrangementType.Crystallised);
        return (
            <>
                <IncomePayment
                    data={
                        this.hasDrawdown(data)
                            ? data?.PensionQuote?.DrawdownModel?.Income
                            : postTransfer?.IncomePaymentDetails?.DrawdownIncome
                                  ?.AnnualIncome
                    }
                    onChange={(changes) => {
                        this.handleOnChange(changes);
                    }}
                    onRef={(ref) => this.appendChild(ref)}
                    pensionBase={data?.PensionQuote}
                    isITFC={isImmediateTaxFreeLumpSum}
                />
                {(data?.PensionQuote?.DrawdownModel?.DrawdownType !== DrawdownType.Regular ||
                    data?.PensionQuote?.PensionTransfers?.some(
                        (e) => e?.PensionSource == PensionArrangementType.Crystallised
                    )) && (
                    <OneOffIncomePayment
                        data={
                            this.hasDrawdown(data) &&
                            data?.PensionQuote?.DrawdownModel?.DrawdownType !== DrawdownType.Regular
                                ? data?.PensionQuote?.DrawdownModel?.OneOffTaxableWithdrawalDetails
                                : data?.PensionQuote?.PensionTransfers[0]?.OneOffTaxableWithdrawalDetails
                        }
                        onChange={(changes) => {
                            this.handleOneOffChange(changes);
                        }}
                        onRef={(ref) => this.appendChild(ref)}
                        pensionBase={data?.PensionQuote}
                        isITFC={isImmediateTaxFreeLumpSum}
                    />
                )}
            </>
        );
    }

    public render(): JSX.Element {
        const { data } = this.props;

        return (
            <div>
                <h2>{quoteSteppersMap.incomePaymentDetails}</h2>
                {data?.PensionQuote?.DrawdownModel?.DrawdownType === DrawdownType.Regular && (
                    <RadiosEditor
                        cssWrapper="rg-inline mb-16"
                        label={quoteSippIncomePaymentDetailsLabels.taxableIncomeRequiredInAddition}
                        selected={data?.PensionQuote?.DrawdownModel?.Income?.TaxableIncomeRequiredInAddition || false}
                        options={[true, false]}
                        getText={(v) => (v ? "Yes" : "No")}
                        onChange={(v) => {
                            this.handleOnChange({ TaxableIncomeRequiredInAddition: v });
                        }}
                    />
                )}
                {this.shouldShowIncomePayment() && this.renderIncomePayment()}
            </div>
        );
    }
}
